import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

// Define available PeerTube instances
export interface PeerTubeInstance {
  name: string;
  url: string;
  apiUrl: string;
}

export const PEERTUBE_INSTANCES: PeerTubeInstance[] = [
  {
    name: 'explainable AI Solutions',
    url: 'https://tube.companion.at',
    apiUrl: 'https://tube.companion.at/api/v1',
  },
];

interface PeerTubeContextType {
  selectedInstance: PeerTubeInstance;
  setSelectedInstance: (instance: PeerTubeInstance) => void;
  instances: PeerTubeInstance[];
}

const PeerTubeContext = createContext<PeerTubeContextType | undefined>(undefined);

interface PeerTubeProviderProps {
  children: ReactNode;
}

export const PeerTubeProvider: React.FC<PeerTubeProviderProps> = ({ children }) => {
  // Try to get the saved instance from localStorage, or default to the first instance
  const [selectedInstance, setSelectedInstance] = useState<PeerTubeInstance>(() => {
    try {
      const savedInstance = localStorage.getItem('selectedPeerTubeInstance');
      if (savedInstance) {
        return JSON.parse(savedInstance) as PeerTubeInstance;
      }
      return PEERTUBE_INSTANCES[0];
    } catch (error) {
      // If there's any error (e.g., invalid JSON), default to first instance
      return PEERTUBE_INSTANCES[0];
    }
  });

  // Save the selected instance to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedPeerTubeInstance', JSON.stringify(selectedInstance));
  }, [selectedInstance]);

  return (
    <PeerTubeContext.Provider
      value={{
        selectedInstance,
        setSelectedInstance,
        instances: PEERTUBE_INSTANCES,
      }}
    >
      {children}
    </PeerTubeContext.Provider>
  );
};

export const usePeerTube = (): PeerTubeContextType => {
  const context = useContext(PeerTubeContext);
  if (context === undefined) {
    throw new Error('usePeerTube must be used within a PeerTubeProvider');
  }
  return context;
};
